import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { StoreValue } from 'effector';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BonusRegistryModel } from 'entities/bonus/model';
import { Bonus } from 'shared/api';
import { translated } from 'shared/lib';
import { accountRoute, gameSettingsRoute } from 'shared/routing';
import { createRegistry, MultiSelect, RegistryRootProps } from 'shared/ui';
import cls from './bonus-registry.module.scss';

export type BonusRegistryProps = RegistryRootProps & {
	withTitle?: boolean;
	withAccount?: boolean;
	tableTdActionSlot?: (bonus: Bonus) => ReactNode;
	topBarActionSlot?: ReactNode;
	emptyActionSlot?: ReactNode;
	filterSlot?: ReactNode;
};

export const createBonusRegistry = ({ model, $games, $currencies }: BonusRegistryModel) => {
	const Registry = createRegistry(model);

	const GamesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'bonus-registry-games-filter',
			className: cls.bonusRegistryGamesFilter,
			data: $games,
			value: model.filters.gameIds.$value.map((value: number[]) => value.map(String)),
			onChange: (value) => model.filters.gameIds.setValue(value.map(Number)),
			label: translated('bonus-registry.filters.games.label'),
			placeholder: translated('bonus-registry.filters.games.placeholder'),
			nothingFoundMessage: translated('bonus-registry.filters.games.empty'),
			clearable: true,
		},
	});

	const CurrenciesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'bonus-registry-currencies-filter',
			className: cls.bonusRegistryCurrencyFilter,
			data: $currencies,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
			label: translated('bonus-registry.filters.currencies.label'),
			placeholder: translated('bonus-registry.filters.currencies.placeholder'),
			nothingFoundMessage: translated('bonus-registry.filters.currencies.empty'),
			clearable: true,
		},
	});

	const AccountLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof accountRoute.$params>>>,
		bind: {
			'data-test-id': 'bonus-registry-account-link',
			to: accountRoute,
			component: Link,
		},
	});

	const GameSettingsLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof gameSettingsRoute.$params>>>,
		bind: {
			'data-test-id': 'bonus-registry-game-link',
			to: gameSettingsRoute,
			component: Link,
		},
	});

	const BonusRegistry = forwardRef<HTMLDivElement, BonusRegistryProps>(
		(
			{
				withTitle = true,
				withAccount = true,
				emptyActionSlot,
				topBarActionSlot,
				filterSlot,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('bonus-registry.title')}</Registry.Title>}
					<Registry.TopBar className={cls.bonusRegistryTopBar}>
						<Registry.Filters className={cls.bonusRegistryFilters}>
							<GamesFilter />
							<CurrenciesFilter />
						</Registry.Filters>
						<div className={cls.bonusRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							{withAccount && <Registry.Table.Col />}
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col className={cls.bonusRegistryColActions} />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								{withAccount && (
									<Registry.Table.Th>{t('bonus-registry.table-columns.account')}</Registry.Table.Th>
								)}
								<Registry.Table.Th>{t('bonus-registry.table-columns.game')}</Registry.Table.Th>
								<Registry.Table.Th>{t('bonus-registry.table-columns.currency')}</Registry.Table.Th>
								<Registry.Table.Th>{t('bonus-registry.table-columns.rate')}</Registry.Table.Th>
								<Registry.Table.Th>{t('bonus-registry.table-columns.threshold')}</Registry.Table.Th>
								<Registry.Table.Th className={cls.bonusRegistryActions}>
									{t('bonus-registry.table-columns.actions')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList
								getProps={(row) => ({
									variant: row.isLocked ? 'locked' : 'default',
								})}
							>
								{(row) => (
									<>
										{withAccount && (
											<Registry.Table.Td>
												<AccountLink query={{ id: row.account.id }}>{row.account.name}</AccountLink>
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>
											<GameSettingsLink query={{ id: row.game.id }}>
												{row.game.title}
											</GameSettingsLink>
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter
												value={row.depositRate * 100}
												suffix=' %'
											/>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.activationThreshold} />
										</Registry.Table.Td>
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('bonus-registry.empty.title')}
						message={t('bonus-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('bonus-registry.table-empty.title')}
						message={t('bonus-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return BonusRegistry;
};
